/**
 * useHealthAlarm.js
 *
 * Custom hook for HealthTicker audio alarms and status transition detection.
 *
 * Behavior:
 * - Detects per-item transitions from not-red to red across API refreshes.
 * - Does NOT ring on first page load or when items stay red.
 * - Synthesizes an alternating two-tone siren (880Hz / 660Hz) using Web Audio API (OscillatorNode + GainNode).
 * - Manages single AudioContext lifecycle lazily on first user interaction.
 * - Persists alarm enabled/disabled in localStorage (default: off).
 * - Updates document.title to "🚨 ALERT – {item names} – JYSWMS" and restores original title on Acknowledge.
 * - Provides banner state: "{item names} turned unhealthy" with time (US Eastern).
 * - Cleans up oscillators, AudioContext, event listeners, and timers on unmount.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY = 'jy_health_alarm_enabled';
const SIREN_FREQ_HIGH = 880;
const SIREN_FREQ_LOW = 660;
const SIREN_CYCLE_MS = 250;
const SAFETY_LIMIT_MS = 60000;

/**
 * Pure function to extract tracked items and their red status.
 *
 * Tracked items:
 * - Top-level /jy-health status === 'unhealthy'
 * - ec2: status !== 'healthy' || total_instances <= 0
 * - rds: status !== 'healthy' || total_databases <= 0
 * - region: status === 'unhealthy' || status === 'error'
 * - netsuite: status !== 'healthy' || failed > 0
 * - priority_sync: status === 'unhealthy' || status === 'error'
 * - alarms: in_alarm_count > 0 || in_alarm.length > 0 || summary.alarm > 0
 * - NetSuite endpoints: any endpoint where is_success === false
 */
export function extractTrackedItemStates(data) {
  if (!data || typeof data !== 'object') return {};
  const states = {};

  // 1. Top-level status
  if (data?.status) {
    const s = String(data.status).toLowerCase();
    const isRed = s === 'unhealthy' || s === 'error' || s === 'failed';
    states['status'] = { id: 'status', name: 'Overall System', isRed };
  }

  // 2. EC2
  if (data?.ec2) {
    const ec2 = data.ec2;
    const ok = ec2?.status === 'healthy' && (ec2?.total_instances ?? 0) > 0;
    states['ec2'] = { id: 'ec2', name: 'EC2', isRed: !ok };
  }

  // 3. RDS
  if (data?.rds) {
    const rds = data.rds;
    const ok = rds?.status === 'healthy' && (rds?.total_databases ?? 0) > 0;
    states['rds'] = { id: 'rds', name: 'Database', isRed: !ok };
  }

  // 4. Region
  if (data?.region_health) {
    const reg = data.region_health;
    const s = String(reg?.status || '').toLowerCase();
    const isRed = s === 'unhealthy' || s === 'error' || s === 'failed';
    states['region'] = { id: 'region', name: 'Region', isRed };
  }

  // 5. NetSuite summary
  if (data?.netsuite_endpoints) {
    const ns = data.netsuite_endpoints;
    const ok = ns?.status === 'healthy' && (ns?.failed ?? 0) === 0;
    states['netsuite'] = { id: 'netsuite', name: 'NetSuite', isRed: !ok };
  }

  // 6. Priority Sync
  if (data?.priority_sync) {
    const sync = data.priority_sync;
    const s = String(sync?.status || '').toLowerCase();
    const isRed = s === 'unhealthy' || s === 'error' || s === 'failed';
    states['priority_sync'] = { id: 'priority_sync', name: 'Priority Sync', isRed };
  }

  // 7. Alarms in alarm
  if (data?.alarms) {
    const alarms = data.alarms;
    const count = Number(alarms?.in_alarm_count) || Number(alarms?.summary?.alarm) || 0;
    const listCount = Array.isArray(alarms?.in_alarm) ? alarms.in_alarm.length : 0;
    const isRed = count > 0 || listCount > 0;
    states['alarms'] = { id: 'alarms', name: 'Alarms', isRed };
  }

  // 8. Individual NetSuite endpoints (any endpoint with is_success false)
  if (Array.isArray(data?.netsuite_endpoints?.endpoints)) {
    data.netsuite_endpoints.endpoints.forEach((ep, i) => {
      const epName = ep?.endpoint || `Endpoint ${i + 1}`;
      const epId = `ep::${ep?.endpoint || i}`;
      const isRed = ep?.is_success === false;
      states[epId] = { id: epId, name: epName, isRed };
    });
  }

  // Also check failed_endpoints list if present
  if (Array.isArray(data?.netsuite_endpoints?.failed_endpoints)) {
    data.netsuite_endpoints.failed_endpoints.forEach((epName) => {
      const epId = `ep::${epName}`;
      if (!states[epId]) {
        states[epId] = { id: epId, name: epName, isRed: true };
      } else {
        states[epId].isRed = true;
      }
    });
  }

  return states;
}

export function formatEasternTime(date = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  } catch {
    return date.toLocaleTimeString();
  }
}

export default function useHealthAlarm(healthData) {
  // Read persisted on/off state from localStorage (default: false / off)
  const [alarmEnabled, setAlarmEnabled] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [isRinging, setIsRinging] = useState(false);
  const [suspendedHint, setSuspendedHint] = useState(false);
  const [bannerInfo, setBannerInfo] = useState(null); // { names: string, time: string } | null

  // Web Audio refs
  const audioCtxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);
  const sirenIntervalRef = useRef(null);
  const safetyTimeoutRef = useRef(null);
  const unlockListenerRef = useRef(null);

  // Status transition tracking refs
  // Map of itemId -> boolean (isRed)
  const prevStatesRef = useRef(null);
  const isFirstFetchRef = useRef(true);

  // Title restoration ref
  const originalTitleRef = useRef(
    typeof document !== 'undefined' ? document.title : ''
  );

  // Keep latest alarmEnabled and isRinging in refs for event listeners and callbacks
  const alarmEnabledRef = useRef(alarmEnabled);
  alarmEnabledRef.current = alarmEnabled;

  const isRingingRef = useRef(isRinging);
  isRingingRef.current = isRinging;

  // ── Audio Context Helper (lazily created and reused) ────────────────────────
  const getAudioContext = useCallback(() => {
    if (typeof window === 'undefined') return null;
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        try {
          audioCtxRef.current = new AudioCtx();
        } catch (e) {
          console.warn('Could not initialize AudioContext', e);
        }
      }
    }
    return audioCtxRef.current;
  }, []);

  // ── Stop Siren Helper ───────────────────────────────────────────────────────
  const stopSiren = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    if (sirenIntervalRef.current) {
      clearInterval(sirenIntervalRef.current);
      sirenIntervalRef.current = null;
    }
    if (oscRef.current) {
      try {
        oscRef.current.stop();
        oscRef.current.disconnect();
      } catch (err) {
        void err;
      }
      oscRef.current = null;
    }
    if (gainRef.current) {
      try {
        gainRef.current.disconnect();
      } catch (err) {
        void err;
      }
      gainRef.current = null;
    }
  }, []);

  // ── Autoplay unlock listener cleanup ────────────────────────────────────────
  const removeUnlockListener = useCallback(() => {
    if (unlockListenerRef.current) {
      window.removeEventListener('click', unlockListenerRef.current, true);
      window.removeEventListener('keydown', unlockListenerRef.current, true);
      unlockListenerRef.current = null;
    }
    setSuspendedHint(false);
  }, []);

  // ── Start Siren (Alternating 880Hz / 660Hz) ─────────────────────────────────
  const startSiren = useCallback(() => {
    stopSiren();

    const ctx = getAudioContext();
    if (!ctx) return;

    // Handle suspended state due to browser autoplay policy
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
      if (ctx.state === 'suspended') {
        setSuspendedHint(true);

        // Setup one-shot user interaction listener to resume and start siren
        if (!unlockListenerRef.current) {
          const handleUnlock = () => {
            removeUnlockListener();
            const currentCtx = getAudioContext();
            if (currentCtx) {
              currentCtx.resume().then(() => {
                if (isRingingRef.current) {
                  startSiren();
                }
              }).catch(() => {});
            }
          };
          unlockListenerRef.current = handleUnlock;
          window.addEventListener('click', handleUnlock, true);
          window.addEventListener('keydown', handleUnlock, true);
        }
        return;
      }
    }

    setSuspendedHint(false);
    removeUnlockListener();

    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(SIREN_FREQ_HIGH, ctx.currentTime);
      gain.gain.setValueAtTime(0.18, ctx.currentTime); // moderate volume

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();

      oscRef.current = osc;
      gainRef.current = gain;

      // Alternating two-tone siren
      let isHigh = true;
      sirenIntervalRef.current = setInterval(() => {
        if (!oscRef.current || !audioCtxRef.current) return;
        isHigh = !isHigh;
        const now = audioCtxRef.current.currentTime;
        oscRef.current.frequency.setValueAtTime(
          isHigh ? SIREN_FREQ_HIGH : SIREN_FREQ_LOW,
          now
        );
      }, SIREN_CYCLE_MS);

      // Safety limit of 60 seconds
      safetyTimeoutRef.current = setTimeout(() => {
        stopSiren();
      }, SAFETY_LIMIT_MS);
    } catch (e) {
      console.warn('Error starting siren:', e);
    }
  }, [getAudioContext, stopSiren, removeUnlockListener]);

  // ── Short 0.3s Test Beep ───────────────────────────────────────────────────
  const playTestBeep = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(SIREN_FREQ_HIGH, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.warn('Error playing test beep:', e);
    }
  }, [getAudioContext]);

  // ── Title helpers ──────────────────────────────────────────────────────────
  const setAlertTitle = useCallback((names) => {
    if (typeof document === 'undefined') return;
    if (!document.title.startsWith('🚨 ALERT')) {
      originalTitleRef.current = document.title;
    }
    document.title = `🚨 ALERT – ${names} – JYSWMS`;
  }, []);

  const restoreTitle = useCallback(() => {
    if (typeof document === 'undefined') return;
    if (document.title.startsWith('🚨 ALERT')) {
      document.title = originalTitleRef.current || 'JYSWMS';
    }
  }, []);

  // ── Trigger Alarm ──────────────────────────────────────────────────────────
  const triggerAlarm = useCallback((unhealthyNames) => {
    const timeStr = formatEasternTime();
    setBannerInfo({
      names: unhealthyNames,
      time: timeStr,
    });

    if (alarmEnabledRef.current) {
      setIsRinging(true);
      setAlertTitle(unhealthyNames);
      startSiren();
    }
  }, [setAlertTitle, startSiren]);

  // ── Acknowledge Action ─────────────────────────────────────────────────────
  const acknowledge = useCallback(() => {
    stopSiren();
    removeUnlockListener();
    setIsRinging(false);
    restoreTitle();
  }, [stopSiren, removeUnlockListener, restoreTitle]);

  // ── Dismiss Visual Banner ──────────────────────────────────────────────────
  const dismissBanner = useCallback(() => {
    setBannerInfo(null);
  }, []);

  // ── Toggle Alarm (Bell button) ─────────────────────────────────────────────
  const toggleAlarm = useCallback(() => {
    setAlarmEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch (e) {
        console.warn('Could not persist alarm setting', e);
      }

      if (next) {
        // Unlocks/resumes AudioContext and plays 0.3s test beep
        playTestBeep();
      } else {
        // Turning off: stop any active ringing immediately
        stopSiren();
        removeUnlockListener();
        setIsRinging(false);
        restoreTitle();
      }
      return next;
    });
  }, [playTestBeep, stopSiren, removeUnlockListener, restoreTitle]);

  // ── Detect Status Transitions when healthData updates ─────────────────────
  useEffect(() => {
    if (!healthData || typeof healthData !== 'object') return;

    const currentStates = extractTrackedItemStates(healthData);
    if (Object.keys(currentStates).length === 0) return;

    // First fetch only records the initial state (NO SOUND, NO ALERT)
    if (isFirstFetchRef.current || !prevStatesRef.current) {
      const initialMap = {};
      Object.keys(currentStates).forEach((id) => {
        initialMap[id] = currentStates[id].isRed ? 'red' : 'green';
      });
      prevStatesRef.current = initialMap;
      isFirstFetchRef.current = false;
      return;
    }

    // Subsequent fetches: detect transitions from not-red to red
    const prevMap = prevStatesRef.current;
    const newlyRedItems = [];

    Object.keys(currentStates).forEach((id) => {
      const isRedNow = currentStates[id].isRed;
      const wasRedBefore = prevMap[id] === 'red';

      if (isRedNow && !wasRedBefore) {
        // Transition detected: was green/unknown/not-red, now red!
        newlyRedItems.push(currentStates[id]);
      }

      // Update state for next cycle
      prevMap[id] = isRedNow ? 'red' : 'green';
    });

    if (newlyRedItems.length > 0) {
      const names = newlyRedItems.map((item) => item.name).join(', ');
      triggerAlarm(names);
    }
  }, [healthData, triggerAlarm]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopSiren();
      removeUnlockListener();
      restoreTitle();

      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch (err) {
          void err;
        }
        audioCtxRef.current = null;
      }
    };
  }, [stopSiren, removeUnlockListener, restoreTitle]);

  return {
    alarmEnabled,
    isRinging,
    suspendedHint,
    bannerInfo,
    toggleAlarm,
    acknowledge,
    dismissBanner,
  };
}
